import React, { useState } from 'react';
import {
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import {
  ChevronLeft,
  ChevronRight,
  Dashboard,
  AccountBalance,
  Person,
  BackupTable,
  StickyNote2,
  Groups,
  AccessTime,
  CalendarMonth,
  EventSeat,
  Mail,
  MarkunreadMailbox,
  Description,
  History as HistoryIcon,
  Info,
  Folder,
  Logout,
  ManageAccounts,
  Redeem,
  Receipt,
  BorderColor,
  Settings,
  AltRoute,
  Lock,
  PlaylistAddCheck,
  StarBorder,
  MenuBook,
  Tune,
} from '@mui/icons-material';
import { AvatarPicture } from './AvatarPicture';
import {
  useMenuSettings,
  useUpdateMenuSettings,
} from '../hooks/reactQuerys/MenuSettingsQueries';
import { MenuSettingsDialog } from './menu/MenuSettingsDialog';
import { HiddenMenuFlyout } from './menu/HiddenMenuFlyout';
import { SupportDialog } from './SupportDialog';
import { IConfig } from '../Interfaces/IConfig';
import { Notifications } from './Notifications';
import { TimeRecordDialog } from '../TimeRecord/TimeRecordDialog';
import { useTheme } from '@mui/material';
import { ChangelogMenu } from '../Changelog/ChangelogMenu';
import { OwnTasks } from '../Task/OwnTasks';
import { useGetConfig } from '../hooks/reactQuerys/ConfigQueries';
import { useGetWordPluginStatus } from '../hooks/reactQuerys/WordPluginAccessQueries';
import { customFetcher } from '../hooks/useFetch';
import { KiChatWidget } from '../Ki/KiChatWidget';

const drawerWidth = 260;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: prop => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

type IProps = {
  children?: React.ReactNode;
  configObject: IConfig;
};

export const AppShell = ({ children, configObject }: IProps) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showSupportDialog, setShowSupportDialog] = useState(false);
  const [showTimeRecordDialog, setShowTimeRecordDialog] = useState(false);

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const [anchorElChangelog, setAnchorElChangelog] =
    React.useState<null | HTMLElement>(null);

  // App/AppSettings werden vom LicenseServer geliefert (config.App). Der
  // Kalender-Menüpunkt erscheint nur, wenn 'calendar' Teil der lizenzierten
  // App-Liste ist.
  const { config: appConfig } = useGetConfig();
  const isCalendarEnabled = (appConfig?.Module ?? []).includes('calendar');

  const isMenuOpen = Boolean(anchorElUser);
  const isChangelogMenuOpen = Boolean(anchorElChangelog);

  const handleLogout = async () => {
    // WICHTIG: Backend-Logout (DELETE /oauth) muss VOR dem Token-Remove
    // laufen, damit der Authorization-Header noch gesetzt ist und das
    // Backend die richtige UserSession-Zeile loeschen kann. Sonst bleibt
    // die alte Session in der DB und erscheint beim Re-Login als zweite
    // aktive Session.
    try {
      await customFetcher({ endpoint: '/oauth', method: 'DELETE' });
    } catch {
      // Idempotent: auch wenn der Backend-Call scheitert (Token bereits
      // abgelaufen, Netzwerk weg), trotzdem lokal abmelden.
    }
    localStorage.removeItem('SessionToken');
    localStorage.removeItem('scope');
    localStorage.removeItem('role');
    window.location.reload();
  };

  const scopes = localStorage.getItem('scope')?.split(',') ?? [];

  // --- Userspezifische Menü-Einstellungen (ausgeblendete Menüpunkte) ---------
  const [showMenuSettings, setShowMenuSettings] = useState(false);
  const { menuSettings } = useMenuSettings();
  const { updateMenuSettings, isUpdatingMenuSettings } =
    useUpdateMenuSettings();

  const isAdmin = !!scopes.find(s => s === 'admin');
  // Dokumentvorlagen gehoeren zum Word-Plugin und sind freischaltbar (#1245).
  const { isWordPluginEnabled } = useGetWordPluginStatus();
  const canBill = !!scopes.find(s => s === 'bill' || s === 'admin');
  const canSystemmails =
    scopes?.find(x => x === 'systemmails' || x === 'admin') !== undefined;
  const canTimerecording =
    !!scopes.find(s => s === 'timerecording' || s === 'admin') &&
    configObject.Module.find(x => x === 'hr') !== undefined;

  // Vollständige Menüdefinition. `visible` kapselt Berechtigung + aktive
  // Erweiterung — nur sichtbare Einträge sind ausblendbar bzw. tauchen im
  // Flyout „Ausgeblendete Elemente" auf.
  const menuItems: {
    key: string;
    label: string;
    icon: React.ReactNode;
    path: string;
    group: number;
    visible: boolean;
  }[] = [
    {
      key: '/',
      label: 'Startseite',
      icon: <Dashboard />,
      path: '/',
      group: 1,
      visible: true,
    },
    {
      key: '/calendar',
      label: 'Kalender',
      icon: <CalendarMonth />,
      path: '/calendar',
      group: 1,
      visible: isCalendarEnabled,
    },
    {
      key: '/calendar/belegungsplan',
      label: 'Belegungsplan',
      icon: <EventSeat />,
      path: '/calendar/belegungsplan',
      group: 1,
      visible: isCalendarEnabled,
    },

    {
      key: '/dialoginbox',
      label: 'Importbereich',
      icon: <MarkunreadMailbox />,
      path: '/dialoginbox',
      group: 2,
      visible: true,
    },
    {
      key: '/order_book',
      label: 'Auftragsbuch',
      icon: <BorderColor />,
      path: '/order_book',
      group: 2,
      visible: true,
    },
    {
      key: '/legaltransaction',
      label: 'Vorgänge',
      icon: <AccountBalance />,
      path: '/legaltransaction',
      group: 2,
      visible: true,
    },
    {
      key: '/archive',
      label: 'Archiv',
      icon: <Lock />,
      path: '/archive',
      group: 2,
      visible: isAdmin,
    },
    {
      key: '/grundstuecke',
      label: 'Grundbuch',
      icon: <MenuBook />,
      path: '/grundstuecke',
      group: 2,
      visible: true,
    },

    {
      key: '/invoices/view/overview',
      label: 'Rechnungen',
      icon: <Receipt />,
      path: '/invoices/view/overview',
      group: 3,
      visible: canBill,
    },
    {
      key: '/task',
      label: 'Aufgaben',
      icon: <PlaylistAddCheck />,
      path: '/task',
      group: 3,
      visible: true,
    },
    {
      key: '/clients',
      label: 'Mandanten',
      icon: <Person />,
      path: '/clients',
      group: 3,
      visible: true,
    },
    {
      key: '/document_portal',
      label: 'Posteingang',
      icon: <Folder />,
      path: '/document_portal',
      group: 3,
      visible: true,
    },
    {
      key: '/tracking/trackings',
      label: 'Nachverfolgungen',
      icon: <AltRoute />,
      path: '/tracking/trackings',
      group: 3,
      visible: true,
    },

    {
      key: '/legaltransactiontypes',
      label: 'Rechtsgeschäftsarten',
      icon: <BackupTable />,
      path: '/legaltransactiontypes',
      group: 4,
      visible: true,
    },
    {
      key: '/textmodules',
      label: 'Vorlagen',
      icon: <StickyNote2 />,
      path: '/textmodules',
      group: 4,
      visible: true,
    },
    {
      key: '/legalclientgroups',
      label: 'Mandantengruppen',
      icon: <Groups />,
      path: '/legalclientgroups',
      group: 4,
      visible: true,
    },

    {
      key: '/timerecording',
      label: 'Zeiterfassung',
      icon: <AccessTime />,
      path: '/timerecording',
      group: 5,
      visible: canTimerecording,
    },
    {
      key: '/daysoffwork',
      label: 'Fehltage',
      icon: <CalendarMonth />,
      path: '/daysoffwork',
      group: 5,
      visible: canTimerecording,
    },

    {
      key: '/system_mail/legal_clients',
      label: 'Systemnachrichten',
      icon: <Mail />,
      path: '/system_mail/legal_clients',
      group: 6,
      visible: canSystemmails,
    },
    {
      key: '/rating',
      label: 'Bewertungsbögen',
      icon: <StarBorder />,
      path: '/rating',
      group: 6,
      visible: canSystemmails,
    },

    {
      key: '/admin/globalmail',
      label: 'Globale E-Mails',
      icon: <MarkunreadMailbox />,
      path: '/admin/globalmail',
      group: 7,
      visible: isAdmin,
    },

    {
      key: '/admin/arnotop',
      label: 'Dokumentvorlagen',
      icon: <Description />,
      path: '/admin/arnotop/entscheidungsbaeume',
      group: 8,
      visible: isAdmin && isWordPluginEnabled,
    },
    {
      key: '/admin/audit',
      label: 'Protokoll',
      icon: <HistoryIcon />,
      path: '/admin/audit',
      group: 8,
      visible: isAdmin,
    },
    {
      key: '/admin/user',
      label: 'Einstellungen',
      icon: <Settings />,
      path: '/admin/user',
      group: 8,
      visible: isAdmin,
    },
    {
      key: '/systeminfo',
      label: 'Systeminformationen',
      icon: <Info />,
      path: '/systeminfo',
      group: 8,
      visible: true,
    },
  ];

  const availableMenuItems = menuItems.filter(m => m.visible);
  const hiddenKeys = menuSettings?.hidden ?? [];
  const hiddenSet = new Set(hiddenKeys);
  const shownMenuItems = availableMenuItems.filter(m => !hiddenSet.has(m.key));
  const hiddenMenuItems = availableMenuItems.filter(m => hiddenSet.has(m.key));
  const menuGroups = Array.from(new Set(shownMenuItems.map(m => m.group))).sort(
    (a, b) => a - b
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar position='fixed' open={isSidebarOpen}>
        <Toolbar>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexGrow: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                size='large'
                edge='start'
                color='inherit'
                aria-label='menu'
                sx={{
                  marginRight: 5,
                  ...(isSidebarOpen && { display: 'none' }),
                }}
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              >
                <MenuIcon />
              </IconButton>

              <Typography
                variant='h6'
                noWrap
                component='div'
                onClick={() => navigate('/')}
                sx={{ cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
                title='Zur Startseite'
              >
                DiNo | {configObject.company_name} | &copy; LeXtorByte
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton
                onClick={(event: React.MouseEvent<HTMLElement>) =>
                  setAnchorElChangelog(event.currentTarget)
                }
              >
                <Redeem sx={{ color: 'white' }} />
              </IconButton>

              <OwnTasks />
              <Notifications />

              <ChangelogMenu
                open={isChangelogMenuOpen}
                onClose={() => setAnchorElChangelog(null)}
                anchorEl={anchorElChangelog}
              />

              <Tooltip title='Einstellungen öffnen'>
                <IconButton
                  onClick={(event: React.MouseEvent<HTMLElement>) =>
                    setAnchorElUser(event.currentTarget)
                  }
                >
                  <AvatarPicture />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={anchorElUser}
                open={isMenuOpen}
                onClose={() => setAnchorElUser(null)}
                sx={{
                  '& .MuiList-padding': {
                    paddingTop: 0,
                  },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              >
                <Typography
                  sx={{
                    textAlign: 'center',
                    color: theme.palette.grey[500],
                    p: 1,
                  }}
                >
                  {configObject.version}
                </Typography>
                <Divider sx={{ m: 0 }} />
                <SupportDialog
                  open={showSupportDialog}
                  onClose={() => setShowSupportDialog(false)}
                />
                <MenuItem onClick={() => setShowSupportDialog(true)}>
                  Support
                </MenuItem>

                <TimeRecordDialog
                  open={showTimeRecordDialog}
                  onClose={() => setShowTimeRecordDialog(false)}
                />
                <MenuItem onClick={() => setShowTimeRecordDialog(true)}>
                  Zeiterfassung
                </MenuItem>

                <Divider sx={{ m: 0, '& .MuiDivider-root': { m: 0 } }} />

                <MenuItem
                  onClick={() => {
                    setAnchorElUser(null);
                    navigate('/account');
                  }}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography textAlign='center'>Mein Konto</Typography>
                  <ListItemIcon>
                    <ManageAccounts fontSize='small' />
                  </ListItemIcon>
                </MenuItem>

                <MenuItem
                  onClick={handleLogout}
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Typography textAlign='center'>Abmelden</Typography>
                  <ListItemIcon>
                    <Logout fontSize='small' />
                  </ListItemIcon>
                </MenuItem>
              </Menu>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      <Drawer
        variant='permanent'
        open={isSidebarOpen}
        onMouseOver={() => setIsSidebarOpen(true)}
        onMouseLeave={() => setIsSidebarOpen(false)}
      >
        <DrawerHeader>
          <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
            {theme.direction === 'rtl' ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        </DrawerHeader>
        {menuGroups.map(group => (
          <React.Fragment key={`menu-group-${group}`}>
            <Divider />
            <List>
              {shownMenuItems
                .filter(item => item.group === group)
                .map(item => (
                  <ListItemButton
                    key={item.key}
                    onClick={() => navigate(item.path)}
                  >
                    <ListItemIcon>{item.icon}</ListItemIcon>
                    <ListItemText>{item.label}</ListItemText>
                  </ListItemButton>
                ))}
            </List>
          </React.Fragment>
        ))}

        <Divider />
        <List>
          {hiddenMenuItems.length > 0 && (
            <HiddenMenuFlyout
              items={hiddenMenuItems}
              onNavigate={path => navigate(path)}
            />
          )}
          <ListItemButton onClick={() => setShowMenuSettings(true)}>
            <ListItemIcon>
              <Tune />
            </ListItemIcon>
            <ListItemText>Menüeinstellungen</ListItemText>
          </ListItemButton>
        </List>
      </Drawer>

      {/*
        `minWidth: 0`: ein Flex-Element darf sonst nicht schmaler werden als sein Inhalt. MAIN wuchs
        dadurch über den Rand, jede rechtsbündige Aktion wurde angeschnitten und die Seite scrollte
        waagrecht (Sichtprüfung #1245, Teil 40). Inhalt, der wirklich breiter ist, läuft weiter
        sichtbar über und bleibt erreichbar.
      */}
      <Box component='main' sx={{ flexGrow: 1, minWidth: 0, p: 3 }}>
        <DrawerHeader />
        {children}
      </Box>

      <MenuSettingsDialog
        open={showMenuSettings}
        onClose={() => setShowMenuSettings(false)}
        items={availableMenuItems.map(({ key, label, icon }) => ({
          key,
          label,
          icon,
        }))}
        hidden={hiddenKeys}
        onSave={async keys => {
          await updateMenuSettings(keys);
        }}
        saving={isUpdatingMenuSettings}
      />

      {/* KI-Assistent (#1336). Liegt in der Hülle und damit über allen Seiten;
          blendet sich selbst aus, solange die KI nicht eingerichtet ist. */}
      <KiChatWidget />
    </Box>
  );
};
